import * as React from 'react';
import { style } from 'typestyle';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ISettingsCoordinatorCtrlScope } from '../../../settings/directives/tbSettingsCoordinator';
import { GeneralSettingsCoordinator } from '../../../settings/general-settings/components/GeneralSettingsCoordinator';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import { ISiteSettingsForSiteAdmin } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ServerSettings, ServerSettingsInstance } from '../../../settings/services/ServerSettings';
import { Sites } from '../../../ts/resources/Sites';
import { IEditableServerSettings } from '../../../ts/ServerApi/types/IEditableServerSettings';
import { SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../ts/services/SiteAuthenticationSettingsService';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { AngularDirective } from '../../../common/react/components/AngularDirective';

const template = `<div ng-include="'siteSettings.html'"></div>`;

export interface GeneralSiteSettingsSubspaceOwnProps extends ReactDirectiveProps{
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface GeneralSiteSettingsSubspaceAngularProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
  readonly siteAuthService: SiteAuthenticationSettingsService;
}

export type GeneralSiteSettingsSubspaceProps = GeneralSiteSettingsSubspaceOwnProps & GeneralSiteSettingsSubspaceAngularProps;

export interface GeneralSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly serverSettings: IEditableServerSettings;
  readonly siteSettings: ISiteSettingsForSiteAdmin;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
}

const subspaceStyle = style({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column'
});

export class GeneralSiteSettingsSubspaceComponent extends React.PureComponent<GeneralSiteSettingsSubspaceProps, GeneralSiteSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope = undefined;
  private unmounted: boolean = false;

  private initialState: GeneralSiteSettingsSubspaceState = {
    hasDataToRender: false,
    hasError: false,
    serverSettings: undefined,
    siteSettings: undefined,
    siteAuthenticationSettings: undefined,
    siteSettingsForServerAdmin: undefined
  };

  public constructor(props: GeneralSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: GeneralSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.serverSettings)
      && !_.isUndefined(this.state.siteSettings)
      && !_.isUndefined(this.state.siteAuthenticationSettings)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)) {
        this.updateState({hasDataToRender: true});
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={FeatureFlags.settingsReactMigration2 ? subspaceStyle : 'tb-stylish-settings'}>
        {FeatureFlags.settingsReactMigration2
          ? <GeneralSettingsCoordinator {...this.state}/>
          : <AngularDirective
            template={template}
            angularContext={this.props.angularContext}
            setupScope={this.setupScope}
          />
        }
      </div>
    );
  }

  private updateState<K extends keyof GeneralSiteSettingsSubspaceState>(newState: Pick<GeneralSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getServerSettings();
    this.getSiteSettings();
    this.getSiteSettingsForServerAdmin();
    this.getSiteAuthenticationSettings();
  }

  private getServerSettings = async () => {
    if (this.props.isSessionServerAdmin) {
      try {
        const serverSettings = await this.props.serverSettingsService.fetch();
        this.updateState({serverSettings: serverSettings});
      } catch (e) {
        this.updateState({hasError: true});
      }
    } else {
      this.updateState({serverSettings: null});
    }
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettingsForServerAdmin = async () => {
    if (this.props.isSessionServerAdmin) {
      try {
        await this.props.server.getSessionInfoPromise();
        const serverSettings = await this.props.sites.getCurrentSiteSettingsForServerAdmin();
        this.updateState({siteSettingsForServerAdmin: serverSettings});
      } catch (e) {
        this.updateState({hasError: true});
      }
    } else {
      this.updateState({siteSettingsForServerAdmin: null});
    }
  }

  private getSiteAuthenticationSettings = async () => {
    try {
      const siteAuthSettings = await this.props.siteAuthService.getSiteAuthenticationSettings(/* refresh */ true);
      this.updateState({siteAuthenticationSettings: siteAuthSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ISettingsCoordinatorCtrlScope;
    childScope.showEncryptionStatus = true;
    childScope.server = this.props.server;
    childScope.hideNameAndId = !this.props.isSessionMultiSite;
    childScope.serverSettings = this.state.serverSettings as IServerAdminSettings;
    childScope.siteSettings = this.state.siteSettings as unknown as ISiteSettingsForSiteAdmin;
    childScope.siteSettingsForServerAdmin = this.state.siteSettingsForServerAdmin;
    childScope.siteAuthenticationSettings = this.state.siteAuthenticationSettings;
    this.childScope = childScope;
    return childScope;
  }
}

const GeneralSiteSettingsHOC = angularProviderHOC<GeneralSiteSettingsSubspaceOwnProps, GeneralSiteSettingsSubspaceAngularProps, GeneralSiteSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    serverSettingsService: ServerSettingsInstance,
    sites: ServiceLocator.get<Sites>('Sites'),
    siteAuthService: ServiceLocator.get<SiteAuthenticationSettingsService>('SiteAuthenticationSettingsService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const GeneralSiteSettingsSubspace: React.ComponentType<GeneralSiteSettingsSubspaceOwnProps>
  = GeneralSiteSettingsHOC(GeneralSiteSettingsSubspaceComponent);
